<template>
    <div>
        <div class="card-bg shadow-md">
            <div class="flex justify-between items-center p-4">
                <div class="flex items-center gap-2">
                    <MultiSelect :loading="isLoading" v-model="selectedUserIds" placeholder="Select Users"
                        :options="users" :maxSelectedLabels="2" optionValue="id" optionLabel="name"
                        selectedItemsLabel="{0} users selected" @change="handleUserSelectedChange" filter />
                    <Button :label="showLastMonth ? 'Show All' : 'Show Last Month'"
                        :icon="showLastMonth ? 'pi pi-calendar-times' : 'pi pi-calendar-plus'"
                        @click="toggleLastMonth" severity="info" />
                </div>
                <div v-if="showLastMonth" class="flex-1 mx-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div v-if="expectedIsLoading" class="text-sm text-blue-700 font-medium">
                        <Skeleton height="1rem" width="200px" />
                    </div>
                    <div v-else class="text-sm text-blue-700 font-medium">
                        Expected Total: {{ formatCurrency(expectedTotal) }} for {{ expectedInvoices }} invoices
                    </div>
                </div>
                <div class="flex items-center gap-2">
                    <Button label="Generate Invoices" icon="pi pi-file-edit" severity="secondary"
                        @click="openGenerateInvoicesDialog" />
                    <Button label="Approve Invoice/s" :disabled="selectedInvoices.length === 0 || isApprovingInvoices"
                        @click="handleApproveSelected" severity="info" :loading="isApprovingInvoices" />
                </div>
            </div>

        </div>
        <div class="flex justify-center my-4">
            <Button :label="showWithdrawals ? 'Show Invoices' : 'Show Withdrawals'" icon="pi pi-money-bill"
                severity="secondary" :class="{ 'p-button-outlined': !showWithdrawals }"
                @click="handleWithdrawalsClick" />
        </div>
        <div class=" justify-center items-center  pt-5">
            <div class="w-full  bg-white rounded-lg shadow-md">
                <DataTable v-if="!showWithdrawals" ref="dataTable" :value="filteredInvoices"
                    v-model:editingRows="editingRows" v-model:selection="selectedInvoices" dataKey="id" scrollable
                    :scrollHeight="'90vh'" editMode="row" class="w-full">
                    <Column :headerStyle="headerStyles" style="width: 3rem">
                        <template #header>
                            <Checkbox v-model="selectAll" @change="handleSelectAll" binary />
                        </template>
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="slotProps">
                            <Checkbox v-model="selectedInvoices" :value="slotProps.data"
                                :disabled="!isInvoiceSelectable(slotProps.data)" />
                        </template>
                    </Column>
                    <Column field="invoiceNumber" header="Invoice N.O" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>
                    <Column field="userName" header="Publisher" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>
                    <Column field="year" header="Year" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>
                    <Column field="monthName" header="Month" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>
                    <Column field="dateIssued" header="Date issued" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>
                    <Column field="payout" header="Payout" style="width: 10%; min-width: 8rem"
                        :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="slotProps">
                            <div>
                                <!-- Call the formatCurrency function, now it always formats in Euro -->
                                <p>{{ formatCurrency(slotProps.data.payout) }}</p> <!-- Output: €6,031.56 -->
                            </div>
                        </template>
                        <template #editor="{ data, field }">
                            <InputNumber prefix="€" v-model="data[field]" fluid />
                        </template>
                    </Column>
                    <Column header="Net Payout" style="width: 10%; min-width: 8rem" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="{ data }">
                            <div>
                                <!-- Call the formatCurrency function, now it always formats in Euro -->
                                <p>{{ formatCurrency(data.netPayout) }}</p> <!-- Output: €6,031.56 -->
                            </div>
                        </template>
                    </Column>
                    <Column field="status" header="Status" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="slotProps">
                            <Tag :value="slotProps.data.status" :severity="getStatusSeverity(slotProps.data.status)" />
                        </template>
                    </Column>
                    <Column field="vatExempt" header="Vat Exempt" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="slotProps">
                            <Tag :value="slotProps.data.vatExempt ? 'Yes' : 'No'"
                                :severity="slotProps.data.vatExempt ? 'success' : 'info'" />
                        </template>
                    </Column>
                    <Column :rowEditor="true" style="width: 12%; min-width: 8rem" bodyStyle="text-align:center"
                        :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="slotProps">
                            <div v-if="!isEditing(slotProps.data.id)" class="flex items-center gap-2">
                                <Button
                                    v-if="slotProps.data.status !== 'approved' && slotProps.data.status !== 'approved'"
                                    icon="pi pi-pencil" severity="secondary" outlined rounded text
                                    @click="onRowEditInit(slotProps.data)" v-tooltip.bottom="'Edit'" />
                                <Button icon="pi pi-eye" severity="info" rounded text
                                    @click="openPreviewDialog(slotProps.data)" v-tooltip.bottom="'Preview'" />
                                <Button v-if="slotProps.data.status === 'approved'" label="View" severity="primary"
                                    @click="openViewDialog(slotProps.data)" v-tooltip.bottom="'View'" rounded />
                            </div>
                            <!-- Save Button -->
                            <div v-if="isEditing(slotProps.data.id)">
                                <Button icon="pi pi-check" text class="mr-1"
                                    @click="openConfirmPayoutUpdate($event, slotProps.data)"
                                    v-tooltip.bottom="'Update Payout'" severity="primary" rounded />

                                <!-- Cancel Button -->
                                <Button icon="pi pi-times" rounded text @click="onRowEditCancel(slotProps.data)"
                                    v-tooltip.bottom="'Cancel edit'" severity="secondary" outlined />
                            </div>
                        </template>
                    </Column>
                    <!-- Footer template that shows when current month filter is active -->
                    <template #footer v-if="showLastMonth">
                        <div class="flex justify-between items-center p-4 bg-gray-800 border-t">
                            <div class="text-sm text-gray-300">
                                <i class="pi pi-calendar mr-2"></i>
                                Showing invoices for last month only
                            </div>
                            <div class="text-sm font-medium text-gray-300">
                                Total: {{formatCurrency(filteredInvoices.reduce((sum, invoice) => sum + invoice.payout, 0)) }}
                            </div>
                            <div class="text-sm font-medium text-gray-300">
                                {{ filteredInvoices.length }} invoice{{ filteredInvoices.length !== 1 ? 's' : '' }} found
                            </div>
                        </div>
                    </template>
                </DataTable>

                <div v-else>
                    <WithdrawalsView />
                </div>
                <ConfirmPopup group="headless">
                    <template #container="{ acceptCallback, rejectCallback }">
                        <div class="rounded p-4">
                            <div class="mb-4 flex flex-col ">
                                <div>
                                    <InputText v-model="updatePayoutComment" class="w-full md:w-3/5 mt-1" fluid
                                        id="updatePayoutComment" placeholder="Optional reason" autofocus />
                                </div>
                            </div>
                            <div class="flex items-center gap-2 mt-4">
                                <Button label="Update" @click="acceptCallback" size="small" severity="primary"
                                    :loading="isUpdatingPayout"></Button>

                            </div>
                        </div>
                    </template>
                </ConfirmPopup>
            </div>
        </div>
        <InvoicePreview v-model:visible="previewDialogVisible" :invoice="selectedInvoiceForPreview"
            :mode="previewMode" />
        <Dialog v-model:visible="generateInvoicesDialogVisible" modal header="Generate Invoices"
            :style="{ width: '40rem', maxWidth: '95vw' }">
            <div class="flex flex-col gap-4">
                <div>
                    <label class="block text-sm font-medium mb-2">Month</label>
                    <DatePicker v-model="selectedInvoiceMonth" view="month" dateFormat="mm/yy" showIcon
                        class="w-full" />
                </div>
                <div>
                    <label class="block text-sm font-medium mb-2">Eligible Users</label>
                    <MultiSelect v-model="selectedInvoiceUserIds" :options="invoiceEligibleUsers"
                        optionLabel="userName" optionValue="userId" placeholder="Select users" filter
                        :loading="invoiceEligibleUsersLoading" :maxSelectedLabels="3"
                        selectedItemsLabel="{0} users selected" class="w-full" />
                    <small class="text-surface-500">
                        Users are loaded from publisher earnings for {{ selectedInvoiceMonthLabel }}.
                    </small>
                </div>
                <div class="text-sm text-surface-600">
                    Range: <strong>{{ selectedInvoiceRange.from }}</strong> to
                    <strong>{{ selectedInvoiceRange.to }}</strong>
                </div>
            </div>
            <template #footer>
                <div class="flex justify-end gap-2">
                    <Button label="Cancel" severity="secondary" outlined
                        @click="generateInvoicesDialogVisible = false" />
                    <Button label="Generate" icon="pi pi-check" :loading="isGeneratingInvoices"
                        :disabled="selectedInvoiceUserIds.length === 0 || invoiceEligibleUsersLoading"
                        @click="handleGenerateInvoices" />
                </div>
            </template>
        </Dialog>
    </div>

</template>

<script setup>
import { ref, computed, nextTick, onMounted, watch } from "vue";
import InvoiceService from "@/services/InvoiceService";
import UsersService from "@/services/UsersService";
import EarningsService from "@/services/EarningsService";
import { headerStyles, nestedHeaderStyles } from '@/global/headerStyles';
import { formatCurrency } from '@/utils/currencies';
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { getStatusSeverity } from '@/utils/wallet-helper';
import Checkbox from 'primevue/checkbox';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import InvoicePreview from '../InvoicePreview.vue';
import { useRouter, useRoute } from 'vue-router';
import WithdrawalsView from './WithdrawalsView.vue';
const confirm = useConfirm();
const router = useRouter();
const route = useRoute();

//loading states
const isLoading = ref(true);
const isUpdatingPayout = ref(false);
const isApprovingInvoices = ref(false);
const isGeneratingInvoices = ref(false);
//end loading states

const toast = useToast();

const invoices = ref(new Array(5));

//users
const users = ref([]);
const selectedUserIds = ref([]);


const handleUserSelectedChange = () => {
    console.log('Selected users changed to', selectedUserIds.value);
};


const showLastMonth = ref(false);
const expectedTotal = ref(0);

const expectedInvoices = ref(0);
const expectedIsLoading = ref(false);
const generateInvoicesDialogVisible = ref(false);
const invoiceEligibleUsersLoading = ref(false);
const selectedInvoiceUserIds = ref([]);

const getLastMonthDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, 1);
};

const selectedInvoiceMonth = ref(getLastMonthDate());

const buildMonthRange = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

    return {
        from: `${year}-${String(month + 1).padStart(2, '0')}-01`,
        to: `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDayOfMonth).padStart(2, '0')}`
    };
};

const selectedInvoiceRange = computed(() => buildMonthRange(selectedInvoiceMonth.value));
const selectedInvoiceMonthLabel = computed(() =>
    selectedInvoiceMonth.value.toLocaleDateString('en-GB', {
        month: 'long',
        year: 'numeric'
    })
);

const invoiceEligibleUsers = ref([]);

const selectedInvoiceMonthNumber = computed(() => selectedInvoiceMonth.value.getMonth() + 1);
const selectedInvoiceYear = computed(() => selectedInvoiceMonth.value.getFullYear());

const userHasInvoiceForSelectedMonth = (userId) => {
    return invoices.value.some(invoice =>
        invoice.userId === userId &&
        invoice.month === selectedInvoiceMonthNumber.value &&
        invoice.year === selectedInvoiceYear.value
    );
};

const removeAlreadyInvoicedSelectedUsers = () => {
    invoiceEligibleUsers.value = invoiceEligibleUsers.value.filter(user =>
        !userHasInvoiceForSelectedMonth(user.userId)
    );

    selectedInvoiceUserIds.value = selectedInvoiceUserIds.value.filter(userId =>
        invoiceEligibleUsers.value.some(user => user.userId === userId)
    );
};

const loadInvoiceEligibleUsers = async () => {
    try {
        invoiceEligibleUsersLoading.value = true;
        const response = await EarningsService.getByPublisher(
            selectedInvoiceRange.value.from,
            selectedInvoiceRange.value.to
        );

        invoiceEligibleUsers.value = response.data
            .filter(item => (item.metrics?.customerTotalRevenue || 0) > 0)
            .filter(item => !userHasInvoiceForSelectedMonth(item.publisher.id))
            .map(item => ({
                userId: item.publisher.id,
                userName: item.publisher.name
            }))
            .filter((user, index, arr) =>
                index === arr.findIndex(item => item.userId === user.userId)
            )
            .sort((a, b) => a.userName.localeCompare(b.userName));

        selectedInvoiceUserIds.value = selectedInvoiceUserIds.value.filter(userId =>
            invoiceEligibleUsers.value.some(user => user.userId === userId)
        );
    } catch (error) {
        console.error('Error fetching invoice eligible users:', error);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to fetch users with commission for the selected month',
            life: 3000
        });
    } finally {
        invoiceEligibleUsersLoading.value = false;
    }
};

const openGenerateInvoicesDialog = async () => {
    selectedInvoiceMonth.value = getLastMonthDate();
    selectedInvoiceUserIds.value = [];
    generateInvoicesDialogVisible.value = true;
    await loadInvoiceEligibleUsers();
};
// Watch for changes in showLastMonth and fetch data if needed
watch(showLastMonth, async (newValue) => {
    if (newValue && expectedTotal.value === null) {
        try {
            expectedIsLoading.value = true;
            const currentDate = new Date();
            const startOfLastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
            const endOfLastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);

            // Format dates as YYYY-MM-DD strings using local timezone
            const startDateString = `${startOfLastMonth.getFullYear()}-${String(startOfLastMonth.getMonth() + 1).padStart(2, '0')}-${String(startOfLastMonth.getDate()).padStart(2, '0')}`;
            const endDateString = `${endOfLastMonth.getFullYear()}-${String(endOfLastMonth.getMonth() + 1).padStart(2, '0')}-${String(endOfLastMonth.getDate()).padStart(2, '0')}`;

            console.log('startOfLastMonth', startDateString);
            console.log('endOfLastMonth', endDateString);
            await EarningsService.getByPublisher(startDateString, endDateString)
                .then((response) => {
                    console.log('response', response.data);
                    const customersWithRevenue = response.data.filter(item => (item.metrics.customerTotalRevenue || 0) > 0);
                    expectedTotal.value = customersWithRevenue.reduce((sum, item) => sum + (item.metrics.customerTotalRevenue || 0), 0);
                    expectedInvoices.value = customersWithRevenue.length;
                }).finally(() => expectedIsLoading.value = false);
            // const response = await InvoiceService.getCurrentMonthExpectedTotal();
            // expectedTotal.value = response.data.expectedTotal;
            // expectedInvoices.value = response.data.invoiceCount;
        } catch (error) {
            console.error('Error fetching expected total:', error);
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch expected total',
                life: 3000
            });
        } finally {
            expectedIsLoading.value = false;
        }
    }
}, { immediate: true });

watch(selectedInvoiceMonth, async () => {
    if (!generateInvoicesDialogVisible.value || !selectedInvoiceMonth.value) {
        return;
    }

    await loadInvoiceEligibleUsers();
});

// Initialize expectedTotal as null
expectedTotal.value = null;

// Add the click handler
const toggleLastMonth = () => {
    showLastMonth.value = !showLastMonth.value;
};

// Update your computed property
const filteredInvoices = computed(() => {
    let filtered = invoices.value;

    // Apply user filter if users are selected
    if (selectedUserIds.value.length > 0) {
        filtered = filtered.filter(invoice =>
            selectedUserIds.value.includes(invoice.userId)
        );
    }

    // Apply current month filter if enabled
    if (showLastMonth.value) {
        console.log('showLastMonth', showLastMonth.value);
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth();//this should automatically be the last month
        const currentYear = currentDate.getFullYear();
        console.log('currentMonth', currentMonth);
        console.log('currentYear', currentYear);
        filtered = filtered.filter(invoice =>
            invoice.month === currentMonth &&
            invoice.year === currentYear
        );
    }

    return filtered;
});

//grid edit shit
const editingRows = ref([]);
const selectedInvoices = ref([]);

const isRowSelectable = (event) => {
    return event.data.status !== 'approved';
};

const isEditing = computed(() => {
    return (rowId) => editingRows.value.find(row => row.id === rowId);
});

const onRowEditInit = (rowData) => {
    editingRows.value = [...editingRows.value, rowData];
};

const updatePayoutComment = ref('');
const openConfirmPayoutUpdate = (event, data) => {
    confirm.require({
        target: event.currentTarget,
        group: 'headless',
        message: 'Are you sure you want to update the payout?',
        accept: async () => {
            await onRowEditSave(data);
        },
        reject: () => {
        }
    });
}

async function onRowEditSave(data) {
    try {
        isUpdatingPayout.value = true;
        var response = await InvoiceService.updateInvoicePayoutById(data.id, data.payout, updatePayoutComment.value);
        if (response.status === 200) {
            const invoice = invoices.value.find(invoice => invoice.id === data.id);
            if (invoice) {
                invoice.payout = data.payout;
            }
            console.log('Payout updated successfully in local invoices ref');
            cancelRowEditById(data.id);
            toast.add({ severity: 'success', summary: 'Payout updated', life: 3000 });
        }
    } catch (error) {
        console.log(error);
        toast.add({ severity: 'error', summary: 'Payout update failed', detail: 'Please refresh and try again', life: 3000 });
    } finally {
        isUpdatingPayout.value = false;
    }
}

function onRowEditCancel(data) {
    cancelRowEditById(data.id);
}

function cancelRowEditById(invoiceId) {
    editingRows.value = editingRows.value.filter(row => row.id !== invoiceId);
}
const fetchInvoices = async () => {

    try {
        const response = await InvoiceService.fetchInvoices();
        invoices.value = response.data;
    // Populate users with unique { id, name } from invoices.value

    const uniqueUsersMap = {};
    invoices.value.forEach(invoice => {
        if (invoice.userId && invoice.userName && !uniqueUsersMap[invoice.userId]) {
            uniqueUsersMap[invoice.userId] = { id: invoice.userId, name: invoice.userName };
        }
    });
    users.value = Object.values(uniqueUsersMap);
    } catch (error) {
        console.log(error);
    }
};



const selectAll = ref(false);

const handleSelectAll = () => {
    if (selectAll.value) {
        // Select all selectable invoices
        selectedInvoices.value = filteredInvoices.value.filter(isInvoiceSelectable);
    } else {
        // Deselect all
        selectedInvoices.value = [];
    }
};

const handleApproveSelected = async () => {
    try {
        isApprovingInvoices.value = true;
        const invoiceIds = selectedInvoices.value.map(invoice => invoice.id);
        const response = await InvoiceService.approveInvoices(invoiceIds);

        if (response.status === 200) {
            // Update invoices by ID
            invoices.value = invoices.value.map(invoice => {
                const updatedInvoice = response.data.invoices.find(updated => updated.id === invoice.id);
                if (updatedInvoice) {
                    return { ...invoice, status: updatedInvoice.status };
                }
                return invoice;
            });
            console.log(invoices.value);
            // Clear selections since they're now approved
            selectedInvoices.value = [];

            toast.add({
                severity: 'success',
                summary: 'Invoices approved',
                life: 3000
            });
        }
    } catch (error) {
        console.log(error);
        toast.add({
            severity: 'error',
            summary: 'Invoices approval failed',
            detail: 'Please refresh and try again',
            life: 3000
        });
    } finally {
        isApprovingInvoices.value = false;
    }
};

const handleGenerateInvoices = async () => {
    try {
        isGeneratingInvoices.value = true;

        const response = await InvoiceService.generateInvoices(
            selectedInvoiceRange.value.from,
            selectedInvoiceRange.value.to,
            selectedInvoiceUserIds.value
        );

        await fetchInvoices();
        removeAlreadyInvoicedSelectedUsers();

        toast.add({
            severity: 'success',
            summary: 'Invoice generation queued',
            detail: response.data?.message || `Prepared ${selectedInvoiceUserIds.value.length} invoice request(s).`,
            life: 3000
        });

        generateInvoicesDialogVisible.value = false;
    } catch (error) {
        console.error('Error generating invoices:', error);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to prepare invoice generation requests',
            life: 3000
        });
    } finally {
        isGeneratingInvoices.value = false;
    }
};

const isInvoiceSelectable = (invoice) => {
    return invoice.status !== 'approved';
};

const previewDialogVisible = ref(false);
const selectedInvoiceForPreview = ref(null);
const previewMode = ref('preview');

const openPreviewDialog = (invoice) => {
    selectedInvoiceForPreview.value = invoice;
    previewMode.value = 'preview';
    previewDialogVisible.value = true;
};

const openViewDialog = (invoice) => {
    selectedInvoiceForPreview.value = invoice;
    previewMode.value = 'view';
    previewDialogVisible.value = true;
};

const showWithdrawals = ref(false);

// Check URL on mount
onMounted(() => {
    showWithdrawals.value = route.query.showWithdrawals === 'true';
});

const handleWithdrawalsClick = () => {
    showWithdrawals.value = !showWithdrawals.value;
    // Update URL with query parameter
    router.push({
        query: {
            ...route.query,
            showWithdrawals: showWithdrawals.value ? 'true' : undefined
        }
    });
};

onMounted(async () => {
    await Promise.all([fetchInvoices()]);
    isLoading.value = false;
});
</script>

<style scoped>
/* Your styles here */
</style>
